import axios from 'axios';
import { auth, googleProvider, signInWithPopup, signOut, getAuthToken } from './firebase';
import featureFlagService from './featureFlagService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
});

// Attach auth token — async so Firebase can refresh the ID token if needed
apiClient.interceptors.request.use(async (config) => {
  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/feature-flags')) {
      localStorage.removeItem('token');
      featureFlagService.clearCache();
      await signOut(auth).catch(() => {});
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (credentials) => {
    const response = await apiClient.post('/api/auth/login', credentials);
    // A prior anonymous or different-account fetch may have cached stale
    // flag values in this tab — force a fresh read for the newly-authed user.
    featureFlagService.clearCache();
    return response.data;
  },

  register: async (userData) => {
    const response = await apiClient.post('/api/auth/register', userData);
    return response.data;
  },

  // Phase 1 — open Google popup and probe the backend.
  // Returns { user } for existing/returning users, or { needsAccountType: true }
  // for brand-new users so the caller can show the role-selection step.
  signInWithGoogle: async ({ userType, inviteCode } = {}) => {
    const { user: firebaseUser } = await signInWithPopup(auth, googleProvider);
    const idToken = await firebaseUser.getIdToken();
    const body = { idToken };
    if (userType)   body.userType   = userType;
    if (inviteCode) body.inviteCode = inviteCode;
    const response = await apiClient.post('/api/auth/social-login', body);
    // Only a completed login (returning user) changes the effective
    // identity — new users hit completeSocialLogin next, which clears it.
    if (!response.data.needsAccountType) featureFlagService.clearCache();
    return response.data;
  },

  // Phase 2 — called after the user picks their role in the UI.
  // Firebase is already signed in from the popup; we just re-use the token.
  completeSocialLogin: async ({ userType, inviteCode }) => {
    const idToken = await auth.currentUser.getIdToken();
    const body = { idToken, userType };
    if (inviteCode) body.inviteCode = inviteCode;
    const response = await apiClient.post('/api/auth/social-login', body);
    featureFlagService.clearCache();
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/api/auth/me');
    // Store fresh JWT if the server rotated one (e.g. after userType promotion)
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data.user;
  },

  logout: async () => {
    await signOut(auth).catch(() => {});
    localStorage.removeItem('token');
    featureFlagService.clearCache();
  },

  verifyEmail: async (token) => {
    const response = await apiClient.post(`/api/auth/verify-email/${token}`);
    return response.data;
  },

  resendVerification: async (email) => {
    const response = await apiClient.post('/api/auth/resend-verification', { email });
    return response.data;
  },
};

export default apiClient;
